"use client";

import React from "react";
import { Phone, Mail, MapPin, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link } from "@/components/common/Link";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { cn } from "@/lib/utils";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-4 rounded-lg border border-primary/30 bg-card/50 p-8 text-center shadow-lg backdrop-blur-xl">
        <div className="flex size-12 items-center justify-center rounded-lg bg-primary/20 text-primary">
          <Mail className="size-6" />
        </div>
        <h3 className="font-default text-2xl font-bold text-card-foreground tracking-tight">Transmission Received</h3>
        <p className="text-muted-foreground font-default">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary/50 hover:bg-primary/10 text-primary">
          Send New Signal
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-6 sm:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default font-medium">Artist Name / Alias</FormLabel>
              <FormControl>
                <Input placeholder="Enter your name" className="bg-background/50 border-border focus-visible:ring-primary/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default font-medium">Digital Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="artist@horizon.com" className="bg-background/50 border-border focus-visible:ring-primary/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" rules={{
      pattern: {
        value: /^[0-9+\-\s()]*$/,
        message: "Invalid phone number format"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-default font-medium">Contact Number</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (555) 000-0000" className="bg-background/50 border-border focus-visible:ring-primary/50" {...field} />
            </FormControl>
            <FormDescription className="text-muted-foreground/60">Optional – for urgent label communications</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-default font-medium">Your Inquiry</FormLabel>
            <FormControl>
              <textarea className={cn("flex min-h-[120px] w-full rounded-lg border border-border bg-background/50 px-3 py-2 text-base shadow-xs transition-all outline-none placeholder:text-muted-foreground/50 focus-visible:border-primary focus-visible:ring-primary/30 focus-visible:ring-[3px] aria-invalid:border-destructive aria-invalid:ring-destructive/20 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm", "resize-y")} placeholder="Tell us about your project or booking request..." {...field} />
            </FormControl>
            <FormDescription className="text-muted-foreground/60">Demos should be submitted via the Artist Submission page.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-primary-foreground font-default font-bold tracking-wider uppercase" size="lg">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Transmitting...
          </> : "Send Message"}
      </Button>
    </Form>;
}
export default function GetInTouch() {
  return <section data-section-id="223899" id="get-in-touch" className="bg-background text-foreground relative overflow-hidden py-24">
      {}
      <div className="pointer-events-none absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 opacity-20 blur-3xl">
        <div className="aspect-square w-[800px] rounded-lg bg-gradient-to-br from-primary via-secondary to-purple-600 animate-pulse" />
      </div>
      <div className="pointer-events-none absolute bottom-0 left-0 translate-y-1/4 -translate-x-1/4 opacity-10 blur-2xl">
        <div className="aspect-square w-[600px] rounded-lg bg-primary" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-secondary mb-3 text-sm font-bold tracking-[0.3em] uppercase font-default">
            Contact the Label
          </h2>
          <h3 className="font-default text-4xl font-black tracking-tighter sm:text-6xl bg-clip-text text-transparent bg-gradient-to-b from-white to-white/60">
            Beyond the Horizon
          </h3>
          <p className="text-muted-foreground mt-4 text-lg font-default leading-relaxed">
            Whether you're an artist looking for a home or a brand seeking a partnership, our team is ready to connect.
          </p>
        </div>

        <div className="mt-16 grid gap-12 lg:grid-cols-3 lg:gap-8">
          {}
          <div className="flex flex-col gap-8 lg:col-span-1">
            <div className="flex items-start gap-4 group">
              
              <div>
                
                
                
              </div>
            </div>

            <div className="flex items-start gap-4 group">
              <div className="flex size-14 shrink-0 items-center justify-center rounded-xl border border-secondary/30 bg-card/40 transition-all group-hover:shadow-[0_0_25px_rgba(255,0,255,0.3)] group-hover:border-secondary">
                <Mail className="text-secondary size-6" />
              </div>
              <div>
                <h4 className="font-default text-xl font-bold tracking-tight">Email HQ</h4>
                <p className="text-muted-foreground mt-1 text-sm font-default">
                  General & Press Inquiries
                </p>
                <Link to="mailto:hello@heavenshorizons.com" className="mt-2 block font-bold text-lg transition-colors hover:text-secondary text-foreground font-default">contact@heavenshorizonrecords.com</Link>
              </div>
            </div>

            <div className="flex items-start gap-4 group">
              <div className="flex size-14 shrink-0 items-center justify-center rounded-xl border border-primary/30 bg-card/40 transition-all group-hover:shadow-[0_0_25px_rgba(0,255,255,0.3)] group-hover:border-primary">
                <MapPin className="text-primary size-6" />
              </div>
              <div>
                <h4 className="font-default text-xl font-bold tracking-tight">Michigan HQ</h4>
                <p className="text-muted-foreground mt-1 text-sm font-default">
                  The Heart of the Great Lakes
                </p>
                <address className="mt-2 block not-italic font-bold text-lg text-foreground font-default">Grand Rapids<br />Michigan, USA</address>
              </div>
            </div>
          </div>

          {}
          <div className="rounded-2xl border border-white/10 bg-card/30 p-6 shadow-2xl backdrop-blur-2xl sm:p-8 lg:col-span-2 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-secondary to-primary opacity-50" />
            <FormProvider formKey="contact_form_contact" formId="contact_form_contact" sectionName="Connect With Us" defaultValues={{
            name: "",
            email: "",
            phone: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              pattern: "phone"
            },
            message: {
              required: true
            }
          }} submitText="Send Message" successMessage="Your message has been beamed to our headquarters. We'll respond shortly.">
              <ContactFormInner />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}